/**
 * Sub-Admins Tab — מנהלי אזור
 *
 * Where the admin hands ONE area (פורטל סטודנטים / תכנון פרישה / משכנתא) to
 * someone else. A section admin edits that area's content, lessons, exam questions
 * and AI rules — and nothing else: no users, no audit log, no revenue, no student
 * PII. The server enforces that list independently of this screen.
 *
 * Two ways in, because both come up in practice:
 *   • create — the person has no account yet; we make one and email them a link to
 *     set their own password. We never generate a password for them to be sent.
 *   • grant  — they already registered; we promote that account.
 *
 * Their login is UNCHANGED from the admin's: password, then the emailed one-time
 * code. The code goes to their own address, which is authorised by an admin having
 * typed it here.
 */

import { FormEvent, useEffect, useState } from 'react'
import {
  AlertCircle,
  Check,
  Mail,
  Plus,
  ShieldCheck,
  ShieldOff,
  UserPlus,
} from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'
import type { SubAdmin } from '../../types'

/** Checkbox row of the assignable areas. Used by all three forms. */
function TrackPicker({
  tracks,
  selected,
  onToggle,
  idPrefix,
}: {
  tracks: { key: string; name: string; isActive: boolean }[]
  selected: string[]
  onToggle: (key: string) => void
  idPrefix: string
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {tracks.map((t) => (
        <label
          key={t.key}
          htmlFor={`${idPrefix}-${t.key}`}
          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
            selected.includes(t.key)
              ? 'border-primary-300 bg-primary-50 text-primary-800'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <input
            id={`${idPrefix}-${t.key}`}
            type="checkbox"
            checked={selected.includes(t.key)}
            onChange={() => onToggle(t.key)}
            className="h-4 w-4"
          />
          <span>{t.name}</span>
          {!t.isActive && <span className="text-xs text-gray-400">(מושבת)</span>}
        </label>
      ))}
    </div>
  )
}

export default function SubAdminsTab() {
  const {
    subAdmins,
    assignableTracks,
    subAdminsLoading,
    subAdminsError,
    fetchSubAdmins,
    createSubAdmin,
    grantSubAdmin,
    updateSubAdminTracks,
    resendSubAdminInvite,
    revokeSubAdmin,
  } = useAdminStore()

  const [notice, setNotice] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Create-new form
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [newTracks, setNewTracks] = useState<string[]>([])

  // Promote-existing form
  const [grantEmail, setGrantEmail] = useState('')
  const [grantTracks, setGrantTracks] = useState<string[]>([])

  // Inline "edit areas" state, keyed by sub-admin id
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTracks, setEditTracks] = useState<string[]>([])

  useEffect(() => {
    void fetchSubAdmins()
  }, [fetchSubAdmins])

  const flash = (message: string) => {
    setNotice(message)
    setTimeout(() => setNotice(null), 5000)
  }

  const toggle = (list: string[], key: string) =>
    list.includes(key) ? list.filter((k) => k !== key) : [...list, key]

  const trackName = (key: string) => assignableTracks.find((t) => t.key === key)?.name || key

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (newTracks.length === 0) {
      setFormError('יש לבחור לפחות אזור אחד')
      return
    }
    setBusy(true)
    try {
      const message = await createSubAdmin({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        tracks: newTracks,
      })
      setEmail('')
      setFirstName('')
      setLastName('')
      setNewTracks([])
      flash(message)
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'שגיאה ביצירת מנהל האזור')
    } finally {
      setBusy(false)
    }
  }

  const handleGrant = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (grantTracks.length === 0) {
      setFormError('יש לבחור לפחות אזור אחד')
      return
    }
    setBusy(true)
    try {
      const message = await grantSubAdmin({ email: grantEmail.trim(), tracks: grantTracks })
      setGrantEmail('')
      setGrantTracks([])
      flash(message)
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'שגיאה בהענקת ההרשאות')
    } finally {
      setBusy(false)
    }
  }

  const startEdit = (row: SubAdmin) => {
    setEditingId(row.id)
    setEditTracks(row.managedTracks)
  }

  const saveEdit = async (id: string) => {
    if (editTracks.length === 0) return
    setBusy(true)
    try {
      await updateSubAdminTracks(id, editTracks)
      setEditingId(null)
      flash('האזורים עודכנו')
    } catch {
      /* error shown from the store */
    } finally {
      setBusy(false)
    }
  }

  const handleRevoke = async (row: SubAdmin) => {
    const ok = window.confirm(
      `להסיר את הרשאות הניהול מ־${row.fullName || row.email}?\n\nהחשבון עצמו יישאר קיים כמשתמש רגיל.`
    )
    if (!ok) return
    setBusy(true)
    try {
      await revokeSubAdmin(row.id)
      flash('ההרשאות הוסרו')
    } catch {
      /* error shown from the store */
    } finally {
      setBusy(false)
    }
  }

  const handleInvite = async (row: SubAdmin) => {
    setBusy(true)
    try {
      flash(await resendSubAdminInvite(row.id))
    } catch {
      /* error shown from the store */
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      {notice && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700" role="status">
          <Check size={16} aria-hidden="true" />
          {notice}
        </div>
      )}
      {(formError || subAdminsError) && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          {formError || subAdminsError}
        </div>
      )}

      {/* ─── What this is ─── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-3 flex items-center gap-3">
          <ShieldCheck className="text-primary-600" size={24} aria-hidden="true" />
          <h2 className="text-xl font-bold text-gray-900">מנהלי אזור</h2>
        </div>
        <p className="text-sm leading-relaxed text-gray-600">
          מנהל אזור מקבל שליטה מלאה על <strong>התוכן וה-AI</strong> של האזורים שהוקצו לו: הנושאים
          והשיעורים, שאלות המבחן, אישיות ה-AI והכללים שלו, ורשימת השאלות שהמשתמשים שאלו.
          הוא <strong>אינו</strong> רואה משתמשים, יומן ביקורת, מנויים והכנסות, קודי כיתה או דוח
          התקדמות תלמידים. הכניסה שלו זהה לשלך — סיסמה ואחריה קוד אימות שנשלח למייל.
        </p>
      </div>

      {/* ─── Create a new section admin ─── */}
      <form onSubmit={handleCreate} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <UserPlus className="text-primary-600" size={22} aria-hidden="true" />
          <h3 className="text-lg font-bold text-gray-900">הוספת מנהל אזור חדש</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="sa-first" className="mb-2 block text-sm font-medium text-gray-700">
              שם פרטי
            </label>
            <input
              id="sa-first"
              className="input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="sa-last" className="mb-2 block text-sm font-medium text-gray-700">
              שם משפחה
            </label>
            <input
              id="sa-last"
              className="input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="sa-email" className="mb-2 block text-sm font-medium text-gray-700">
              כתובת דוא"ל
            </label>
            <input
              id="sa-email"
              type="email"
              dir="ltr"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              לכתובת הזו יישלחו גם קישור להגדרת סיסמה וגם קוד האימות בכל כניסה.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <span className="mb-2 block text-sm font-medium text-gray-700">אזורים בניהולו</span>
          <TrackPicker
            tracks={assignableTracks}
            selected={newTracks}
            onToggle={(key) => setNewTracks((prev) => toggle(prev, key))}
            idPrefix="sa-new"
          />
        </div>

        <button type="submit" disabled={busy} className="btn btn-primary mt-4 disabled:opacity-50">
          <Plus size={16} aria-hidden="true" className="ms-1" />
          יצירה ושליחת הזמנה
        </button>
      </form>

      {/* ─── Promote an existing account ─── */}
      <form onSubmit={handleGrant} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-1 text-lg font-bold text-gray-900">הענקת הרשאות למשתמש קיים</h3>
        <p className="mb-4 text-sm text-gray-600">
          למי שכבר נרשם לאפליקציה. החשבון הופך למנהל אזור ושומר על הסיסמה הקיימת שלו.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="sa-grant-email" className="mb-2 block text-sm font-medium text-gray-700">
              כתובת הדוא"ל של המשתמש
            </label>
            <input
              id="sa-grant-email"
              type="email"
              dir="ltr"
              className="input"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <span className="mb-2 block text-sm font-medium text-gray-700">אזורים בניהולו</span>
            <TrackPicker
              tracks={assignableTracks}
              selected={grantTracks}
              onToggle={(key) => setGrantTracks((prev) => toggle(prev, key))}
              idPrefix="sa-grant"
            />
          </div>
        </div>

        <button type="submit" disabled={busy} className="btn btn-secondary mt-4 disabled:opacity-50">
          הענקת הרשאות
        </button>
      </form>

      {/* ─── Existing section admins ─── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">
            מנהלי אזור קיימים {subAdmins.length > 0 && `(${subAdmins.length})`}
          </h3>
        </div>

        {subAdminsLoading && subAdmins.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">טוען…</div>
        ) : subAdmins.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">עדיין לא הוגדרו מנהלי אזור.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {subAdmins.map((row) => (
              <li key={row.id} className="px-6 py-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{row.fullName || '—'}</p>
                    <p className="text-sm text-gray-500" dir="ltr">
                      {row.email}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      אזורים: {row.managedTracks.map(trackName).join(', ') || '—'}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {row.lastLoginAt
                        ? `כניסה אחרונה: ${new Date(row.lastLoginAt).toLocaleDateString('he-IL')}`
                        : 'טרם התחבר/ה'}
                      {row.status !== 'active' && ` · סטטוס: ${row.status}`}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => (editingId === row.id ? setEditingId(null) : startEdit(row))}
                      className="btn btn-outline !px-3 !py-1.5 text-sm"
                    >
                      עריכת אזורים
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInvite(row)}
                      disabled={busy}
                      className="btn btn-outline !px-3 !py-1.5 text-sm disabled:opacity-50"
                      title="שליחת קישור להגדרת סיסמה"
                    >
                      <Mail size={14} aria-hidden="true" className="ms-1" />
                      איפוס סיסמה
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevoke(row)}
                      disabled={busy}
                      className="btn !px-3 !py-1.5 text-sm border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <ShieldOff size={14} aria-hidden="true" className="ms-1" />
                      הסרת הרשאות
                    </button>
                  </div>
                </div>

                {editingId === row.id && (
                  <div className="mt-4 rounded-lg bg-gray-50 p-4">
                    <TrackPicker
                      tracks={assignableTracks}
                      selected={editTracks}
                      onToggle={(key) => setEditTracks((prev) => toggle(prev, key))}
                      idPrefix={`sa-edit-${row.id}`}
                    />
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => saveEdit(row.id)}
                        disabled={busy || editTracks.length === 0}
                        className="btn btn-primary !px-3 !py-1.5 text-sm disabled:opacity-50"
                      >
                        שמירה
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="btn btn-outline !px-3 !py-1.5 text-sm"
                      >
                        ביטול
                      </button>
                    </div>
                    {editTracks.length === 0 && (
                      <p className="mt-2 text-xs text-red-600">
                        חייב להישאר לפחות אזור אחד — להסרה מלאה השתמש/י ב"הסרת הרשאות".
                      </p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
